import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Mail, Send, X, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";

const NAVY = "#010082";

function RecipientGroup({ label, emails, tone = "navy" }) {
  const hasAny = emails && emails.length > 0;
  const toneStyles =
    tone === "amber"
      ? { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-800", chip: "bg-amber-100 text-amber-900 border-amber-200" }
      : tone === "emerald"
      ? { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-800", chip: "bg-emerald-100 text-emerald-900 border-emerald-200" }
      : { bg: "bg-stone-50", border: "border-stone-200", text: "text-stone-800", chip: "bg-white text-stone-800 border-stone-300" };
  return (
    <div
      className={`rounded-md border ${toneStyles.border} ${toneStyles.bg} p-3`}
      data-testid={`digest-preview-group-${label.toLowerCase()}`}
    >
      <div className={`text-[10px] uppercase tracking-widest font-semibold ${toneStyles.text} mb-2`}>{label}</div>
      {hasAny ? (
        <div className="flex flex-wrap gap-1.5">
          {emails.map((e) => (
            <span
              key={e}
              className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${toneStyles.chip}`}
              data-testid={`digest-preview-email-${e}`}
            >
              <Mail className="h-3 w-3 opacity-60" /> {e}
            </span>
          ))}
        </div>
      ) : (
        <div className="text-[11px] italic text-stone-400">None configured</div>
      )}
    </div>
  );
}

export default function DigestPreviewModal({ listingId, open, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open || !listingId) return;
    setLoading(true);
    setData(null);
    (async () => {
      try {
        const { data: resp } = await api.post(`/listings/${listingId}/preview-digest`, {});
        setData(resp);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not build digest preview.");
        onOpenChange(false);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, listingId, onOpenChange]);

  const iframeSrcDoc = useMemo(() => {
    if (!data?.html) return null;
    return (
      `<!doctype html><html><head><meta charset="utf-8"/><base target="_blank"/>` +
      `<style>body{margin:0;padding:16px;background:#f8f5ef;font-family:Arial,sans-serif;color:#1c1917}</style>` +
      `</head><body>${data.html}</body></html>`
    );
  }, [data]);

  const sendNow = async () => {
    if (!data || !data.to || data.to.length === 0) {
      toast.error("No recipients to send to.");
      return;
    }
    setSending(true);
    try {
      const { data: resp } = await api.post(`/listings/${listingId}/send-test-digest`, {
        recipients: data.recipient_tags,
      });
      toast.success(`Digest sent to ${resp.to.length} recipient${resp.to.length === 1 ? "" : "s"}`);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send the digest.");
    } finally {
      setSending(false);
    }
  };

  const totalCount =
    (data?.recipients?.agent?.length || 0) +
    (data?.recipients?.seller?.length || 0) +
    (data?.recipients?.tc?.length || 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl w-[94vw] max-h-[90vh] p-0 overflow-hidden flex flex-col bg-[#faf7f2]"
        data-testid="digest-preview-modal"
      >
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-stone-200 bg-white">
          <DialogTitle className="font-heading text-2xl text-stone-900 tracking-tight">
            Daily Digest Preview
          </DialogTitle>
          <p className="text-xs text-stone-500 mt-1">
            Below is the exact email that will be delivered. Review the recipient list, then send or cancel.
          </p>
        </DialogHeader>

        <div className="px-6 py-4 border-b border-stone-200 bg-white">
          <div className="flex items-baseline justify-between gap-3 mb-2">
            <div className="text-[11px] uppercase tracking-widest font-semibold text-stone-500">
              Resolved recipients
            </div>
            <div className="text-[11px] text-stone-500 tabular-nums" data-testid="digest-preview-total-count">
              {totalCount} total · {data?.intake_status === "draft" ? "Intake is a draft" : "Intake complete"}
            </div>
          </div>
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-stone-500 py-6">
              <Loader2 className="h-4 w-4 animate-spin" /> Building preview…
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <RecipientGroup label="Agent" emails={data?.recipients?.agent || []} tone="navy" />
              <RecipientGroup label="Seller" emails={data?.recipients?.seller || []} tone="emerald" />
              <RecipientGroup label="TC" emails={data?.recipients?.tc || []} tone="amber" />
            </div>
          )}
        </div>

        <div className="flex-1 overflow-hidden bg-stone-100 px-6 py-4">
          <div className="h-full rounded-md border border-stone-200 bg-white overflow-hidden shadow-sm">
            {loading ? (
              <div className="h-[480px] flex items-center justify-center text-sm text-stone-400">
                <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading preview…
              </div>
            ) : (
              <iframe
                title="Digest preview"
                className="w-full h-[480px] border-0"
                sandbox="allow-popups allow-popups-to-escape-sandbox"
                srcDoc={iframeSrcDoc || "<p>No preview available.</p>"}
                data-testid="digest-preview-iframe"
              />
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-stone-200 bg-white flex items-center justify-between gap-3 flex-wrap">
          <div className="text-[11px] text-stone-500 leading-relaxed max-w-md">
            Clicking <strong>Send now</strong> dispatches this exact email to every recipient above.
            This is a real send — the inboxes you see are the inboxes we'll notify.
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="h-9 rounded-full border-stone-300 text-xs"
              data-testid="digest-preview-cancel"
            >
              <X className="h-3.5 w-3.5 mr-1" /> Cancel
            </Button>
            <Button
              type="button"
              onClick={sendNow}
              disabled={sending || loading || totalCount === 0}
              className="h-9 rounded-full px-5 text-white text-xs"
              style={{ backgroundColor: NAVY }}
              data-testid="digest-preview-send"
            >
              <Send className="h-3.5 w-3.5 mr-1.5" />
              {sending ? "Sending…" : `Send to ${totalCount} recipient${totalCount === 1 ? "" : "s"}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
